import React, { useEffect, useRef, useState } from 'react';
import { canPlayGame, isGamePlayedToday } from '../utils/storage';
import { downloadAudio } from '../utils/audioConverter';
import BottomNav from './BottomNav';
import { trackEvent } from '../utils/analytics';
import { getRemainingAIScore, canUseAIScore } from '../utils/premiumGate';
import PremiumPrompt from './PremiumPrompt';

export default function ScoreScreen({ data, onPlayGame, onHome, onBack, onRetry, onNav, isPremium, isGuest, onUpgrade, childId }) {
  const [showDetails, setShowDetails] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const audioRef = useRef(null);
  const gameUnlocked = canPlayGame(childId);
  const gamePlayedToday = isGamePlayedToday(childId);

  const handlePlay = () => {
    if (!data.audioUrl) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      const audio = new Audio(data.audioUrl);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => setPlaying(false);
      setPlaying(true);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    // GA4: スコア閲覧イベント
    trackEvent('ondoku_score_viewed', {
      mode: data?.mode ?? '',
      score: data?.avgScore ?? 0,
      stars: data?.totalStars ?? 0,
      text_title: data?.title || '（タイトルなし）',
    });
    // GA4: ゲーム解放イベント（ほんばんで60点以上・今日初回）
    if (data?.mode === 'honban' && (data?.avgScore ?? 0) >= 60 && canPlayGame(childId)) {
      trackEvent('ondoku_game_unlocked', { score: data.avgScore });
    }
    return () => {
      clearTimeout(t);
      audioRef.current?.pause();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    if (!data.audioUrl) return;
    setDownloading(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await downloadAudio(data.audioUrl, `${data.title ?? 'ondoku'}_${date}`);
    } catch (e) {
      console.error('ダウンロードエラー:', e);
      alert('ダウンロードに　しっぱいしました。もういちど　ためしてね。');
    }
    setDownloading(false);
  };

  const starStr = (n) => '⭐'.repeat(Math.min(n, 3)) + '☆'.repeat(Math.max(3 - n, 0));

  return (
    <div className="screen score-screen">
      {/* もどるボタン */}
      {onBack && (
        <button className="btn-back" onClick={onBack}>← もどる</button>
      )}

      {/* 上限到達メッセージ */}
      {data.limitReached && (
        <div className="score-limit-msg">
          <div className="score-limit-icon">🤖</div>
          <div className="score-limit-text">{data.limitMsg}</div>
        </div>
      )}

      {/* タイトル */}
      <h2 className="score-title">
        {data.limitReached ? '📖 おつかれさま！' : data.avgScore >= 80 ? '🎉 すごい！' : data.avgScore >= 60 ? '😊 よくできた！' : '😄 がんばった！'}
      </h2>

      {/* スコア表示 */}
      {!data.limitReached && (
      <div className={`score-circle ${animated ? 'pop-in' : ''}`}>
        <div className="score-num">{data.avgScore}</div>
        <div className="score-label">てん</div>
      </div>
      )}

      <div className="score-stars-row">
        <div />
        <div className="score-stars">{starStr(data.totalStars)}</div>
        {onRetry
          ? <button className="btn-retry-mini" onClick={() => {
              if (!canUseAIScore(isPremium, isGuest)) {
                trackEvent('ondoku_daily_limit_reached', { is_premium: isPremium, is_guest: isGuest });
                setShowPremiumPrompt(true);
                return;
              }
              onRetry();
            }}>もう一回</button>
          : <div />
        }
      </div>

      {/* AI採点 残り回数（シンプル表示） */}
      {onRetry && (
        <div className="score-ai-remain">
          {canUseAIScore(isPremium, isGuest)
            ? <>🎯 ほんばん採点 {isGuest ? 'のこり' : '今日あと'} <strong>{getRemainingAIScore(isPremium, isGuest)}</strong> 回</>
            : isPremium
              ? '🎯 今日のほんばん採点は終わり！また明日！'
              : isGuest
                ? <span>🎯 おためしのほんばん採点は終わり <button className="ai-score-upgrade-btn" onClick={onUpgrade}>ログインする</button></span>
                : <span>🎯 今日のほんばん採点は終わり <button className="ai-score-upgrade-btn" onClick={onUpgrade}>👑 プレミアムにしよう</button></span>
          }
        </div>
      )}

      {/* 文字起こし結果 */}
      {data.transcript && (
        <div className="transcript-box">
          <p className="transcript-label">📝 きこえた　ことば：</p>
          <p className="transcript-text">{data.display || data.transcript}</p>
        </div>
      )}

      {/* フィードバック */}
      {data.feedback && (
        <div className="feedback-box">
          <p>{data.feedback}</p>
        </div>
      )}

      {/* 詳細コメント（ほんばんモードのみ） */}
      {data.details && data.mode === 'honban' && (
        <div className="details-comment-box">
          <p>{data.details}</p>
        </div>
      )}

      {/* 録音再生・ダウンロード */}
      {data.audioUrl && (
        <div className="audio-actions">
          <button className="btn-play" onClick={handlePlay}>
            {playing ? '⏹ とめる' : '▶️ じぶんの　こえを　きく'}
          </button>
          <button className="btn-download" onClick={handleDownload} disabled={downloading}>
            {downloading ? '⏳ ほぞんちゅう…' : '⬇️ ダウンロード'}
          </button>
        </div>
      )}

      {/* 行ごとの詳細（れんしゅうモード） */}
      {data.lineResults && (
        <button className="btn-details" onClick={() => setShowDetails(!showDetails)}>
          {showDetails ? '▲ とじる' : '▼ くわしく　みる'}
        </button>
      )}
      {showDetails && data.lineResults && (
        <div className="details-list">
          {data.lineResults.map((r, i) => (
            <div key={i} className="detail-row">
              <span className="detail-stars">{starStr(r?.stars ?? 0)}</span>
              <span className="detail-text">{r?.transcript ?? '—'}</span>
            </div>
          ))}
        </div>
      )}

      {/* アクションボタン */}
      <div className="score-actions">
        {gameUnlocked && (
          <button className="btn-game" onClick={onPlayGame}>
            🚀 ゲームで　あそぶ！
          </button>
        )}
        {gamePlayedToday && (
          <div className="game-locked-hint">
            🎮 今日の　ゲームは　おわりだよ！　また　あした！
          </div>
        )}
        {!gameUnlocked && !gamePlayedToday && data.mode === 'renshyu' && (
          <div className="game-locked-hint">
            🔒 ほんばんを　60てん以上で　クリアすると　ゲームが　あそべるよ！
          </div>
        )}
        {!gameUnlocked && !gamePlayedToday && data.mode === 'honban' && (data.avgScore ?? 0) < 60 && (
          <div className="game-locked-hint">
            🔒 60てん以上で　よめると　ゲームが　あそべるよ！　もう一度　チャレンジ！
          </div>
        )}
      </div>

      {/* BottomNav */}
      {onNav && <BottomNav active="home" onNav={onNav} />}

      {showPremiumPrompt && (
        <PremiumPrompt
          isPremium={isPremium}
          isGuest={isGuest}
          onClose={() => setShowPremiumPrompt(false)}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
}
